import React, { useState, useCallback, useEffect } from 'react';

import { FilterAutocomplete } from 'atoms/TableFilter';
import { StyledPaper } from 'pages/ContactsPage/Contacts.styles';
import { StyledFilterWrapper } from 'molecules/Filter/Filter.styles';
import { Stack } from '@mui/material';

import axios, { AxiosResponse } from 'axios';
// import dayjs from 'dayjs';
import { debounce } from 'lodash-es';
import { IZohoProject } from 'pages/CompanyPage/types';
import Filter from 'organisms/Filter';
import { IFilterOption, IFilterOptionItem } from 'organisms/Filter/types';
import ZohoProjectsTable from 'organisms/ProjectsTable/ZohoProjectsTable';
import { StyledReportCSVButton } from 'organisms/CloudOpsReportAdmin/CloudOpsReportAdmin.styles';
import { ReactComponent as CSVIcon } from 'assets/icons/csv.svg';
import { useBaoSelector } from 'utils/hooks/redux';
import {
  ModalActions,
  ModalCloseButton,
  ModalSubmitButton,
} from 'molecules/Modal/Modal.styles';
import Modal from 'molecules/Modal';
import { palette } from 'utils/styles/variables';

interface IParams {
  page: number;
  limit: number;
  search?: string;
  invoice_automation?: string;
  contract_type?: string;
  invoice_creation_approval?: string;
  billing_data_check?: string;
}

type FilterOptionsResponse = {
  contract_type: IFilterOptionItem[];
  billing_data_check: IFilterOptionItem[];
  invoice_creation_automation: string[];
  integrated: string[];
  group: string[];
  status: string[];
  invoice_creation_approval: string[];
};

type FilterOptions = {
  contract_type: IFilterOptionItem[];
  billing_data_check: IFilterOptionItem[];
  invoice_creation_automation: IFilterOptionItem[];
  integrated: IFilterOptionItem[];
  group: IFilterOptionItem[];
  status: IFilterOptionItem[];
  invoice_creation_approval: IFilterOptionItem[];
};

export type filterApplyType = {
  contract_type: string;
  billing_data_check: string;
  invoice_creation_automation: string;
  group: string;
  status: string;
  invoice_creation_approval: string;
  integrated: string;
};

const customRenderer = ({ id, name }: IFilterOptionItem) => (
  <span
    style={{
      color: id === 'no_errors' ? palette.success[500] : palette.error[450],
    }}
  >
    {name}
  </span>
);

const fetchList = async (name: string) => {
  const response: AxiosResponse<FilterOptionsResponse> = await axios.get(
    `${process.env.REACT_APP_USER_SERVICE}/projects/search-params`
  );

  const filterOptions = Object.entries(response?.data).reduce(
    (acc, [key, value]) => {
      if (key === 'contract_type' || key === 'billing_data_check')
        acc[key] = value as IFilterOptionItem[];
      else
        acc[key as keyof FilterOptions] = value.map((val) => ({
          id: val,
          name: val,
        })) as IFilterOptionItem[];

      return acc;
    },
    {} as FilterOptions
  );

  return (filterOptions?.[name as keyof FilterOptions] ||
    []) as IFilterOptionItem[];
};

const initialOptions: Array<IFilterOption> = [
  { code: 'group', title: 'Group', fetchList, active: true },
  { code: 'status', title: 'Status', fetchList },
  { code: 'integrated', title: 'Integrated status', fetchList },
  { code: 'contract_type', title: 'Contract type', fetchList },
  {
    code: 'invoice_creation_automation',
    title: 'Auto-invoicing',
    fetchList,
  },
  {
    code: 'billing_data_check',
    title: 'Billing Data Check',
    fetchList,
    render: customRenderer,
  },
];

const ZohoProjects = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [multiFilter, setMultiFilter] = useState<Partial<filterApplyType>>({});
  const [search, setSearch] = useState<string | undefined>('');
  const [data, setData] = useState<{
    projects: IZohoProject[];
    count: number;
  }>({ projects: [], count: 0 });

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [loadingFile, setLoadingFile] = useState<boolean>(false);

  const { value: user } = useBaoSelector((state) => state.user);

  const handleFilterChange = useCallback(
    (nextFilterValue: { [key: string]: string[] }) => {
      const filterApply = Object?.entries(nextFilterValue).reduce(
        (acc, [key, value]) => {
          if (key === 'contract_type' && value[0]) {
            acc.contract_type = value?.join(',');
          }

          if (key === 'invoice_creation_automation' && value[0]) {
            acc.invoice_creation_automation = value?.join(',');
          }

          if (key === 'group' && value[0]) {
            acc.group = value?.join(',');
          }

          if (key === 'status' && value[0]) {
            acc.status = value?.join(',');
          }

          if (key === 'integrated' && value[0]) {
            acc.integrated = value?.join(',');
          }

          if (key === 'billing_data_check' && value[0]) {
            acc.billing_data_check = value?.join(',');
          }

          return acc;
        },
        {} as filterApplyType
      );

      if (JSON.stringify(filterApply) === JSON.stringify(multiFilter)) return;

      setMultiFilter(filterApply);
    },
    [search, multiFilter]
  );

  /** debounce handler for two types: textfield and autocomplete */
  const debouncedChangeHandler = useCallback(
    debounce((event, value) => {
      setSearch(value || event?.target?.value || '');
    }, 1000),
    []
  );

  const handleConfirmOpen = useCallback(() => {
    setShowConfirmModal(true);
    document.body.setAttribute('id', 'body');
  }, []);

  const handleConfirmClose = useCallback(() => {
    setShowConfirmModal(false);
    document.body.setAttribute('id', '');
  }, []);

  const onConfirmSubmit = useCallback(async () => {
    try {
      setLoadingFile(true);
      await axios.get(
        `${process.env.REACT_APP_USER_SERVICE}/projects/billing-data`,
        {
          params: {
            email: user.email,
          },
        }
      );
    } finally {
      setLoadingFile(false);
      handleConfirmClose();
    }
  }, [user?.email]);

  const fetchPageData = async (
    pagination: { page: number; limit: number },
    sort?: string
  ) => {
    try {
      setLoading(true);
      const { projects, count } = await fetchProjectsList(pagination, sort);

      setData({ projects: projects || [], count: count || 0 });
      return { projects: projects || [], count: count || 0 };
    } finally {
      setLoading(false);
    }
  };

  async function fetchProjectsList(
    pagination: { page: number; limit: number },
    sort?: string
  ) {
    const params: IParams = {
      ...prepareParams(),
      page: pagination.page || 1,
      limit: pagination.limit || 20,
    };

    const response: AxiosResponse<{
      projects: IZohoProject[];
      count: number;
    }> = await axios.get(
      `${process.env.REACT_APP_USER_SERVICE}/projects/zoho?${
        sort || 'sort[name]=asc'
      }`,
      { params }
    );

    return {
      projects: response.data.projects || [],
      count: response.data.count || 0,
    };
  }

  function prepareParams() {
    return {
      ...(search?.trim() ? { search } : {}),
      ...(multiFilter || {}),
    };
  }

  useEffect(() => {
    fetchPageData({ page: 1, limit: 20 });
  }, [search, multiFilter]);

  useEffect(
    () => () => {
      document.body.setAttribute('id', '');
    },
    []
  );

  return (
    <StyledPaper sx={{ '.MuiCardContent-root': { p: '0 !important' } }}>
      <StyledFilterWrapper
        sx={{
          '.filter-popover-container': {
            top: '90px',
            left: '30px',
          },
        }}
      >
        <Stack direction="row" gap={3}>
          <Filter
            options={initialOptions}
            onChange={handleFilterChange}
            loading={loading}
          />

          <FilterAutocomplete
            isFreeSolo={!!search}
            value={search}
            options={[]}
            placeholder="Search by: Project name"
            tooltip="Search by: Project name"
            onChange={debouncedChangeHandler}
            onReset={() => setSearch('')}
          />
        </Stack>
        <StyledReportCSVButton
          onClick={handleConfirmOpen}
          startIcon={<CSVIcon />}
        />
      </StyledFilterWrapper>

      <ZohoProjectsTable
        loading={loading}
        data={data}
        onChange={fetchPageData}
      />

      <Modal
        open={showConfirmModal}
        maxWidth="sm"
        title="Send projects report CSV"
        subtitle={`The report will list all projects from Zoho Projects and any issues blocking invoice creation. It will be sent to your email: ${
          user?.email || ''
        }.`}
        onClose={handleConfirmClose}
        sx={{ zIndex: 33333 }}
      >
        <ModalActions>
          <ModalCloseButton
            autoFocus
            variant="outlined"
            onClick={handleConfirmClose}
          >
            Cancel
          </ModalCloseButton>
          <ModalSubmitButton
            autoFocus
            size="medium"
            variant="contained"
            loading={loadingFile}
            onClick={onConfirmSubmit}
          >
            Send
          </ModalSubmitButton>
        </ModalActions>
      </Modal>
    </StyledPaper>
  );
};

export default ZohoProjects;
